<template>
  <div
    class="stop-view-edit"
    :aria-invalid="!!errorResponse ? 'true' : 'false'"
  >
    <LocationTracker
      v-if="stops.length > 1"
      :stop-type="internalValue.stopType"
      :sequence-number="internalValue.sequenceNumber"
      :mileage="mileage"
      :has-next-stop="hasNextStop"
    />
    <StopEdit
      :id="`stop-${sequenceNumber}`"
      v-model="internalValue"
      :stops="stops"
      :error-response="errorResponse"
      :allow-changing-stop-type="allowChangingStopType"
      :disabled="disabled"
      :removable="removable"
      :show-order-reference-numbers="showOrderReferenceNumbers"
      expanded
      class="flex-1"
      @autocomplete="emit('autocomplete', $event)"
      @remove="emit('remove', $event)"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRef, toRefs } from 'vue';

import { useModelValue } from '@rygen/rygen-ui/composables';
import { ErrorResponse, Loadable } from '@rygen/rygen-ui/model';

import { OrderShippingPoint, StopMileageResponse } from '@/model';

import StopEdit from '@/components/entry/StopEdit.vue';
import { type Stop } from '@/components/entry/StopEntrySection.vue';
import LocationTracker from '@/components/location/LocationTracker.vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<Stop>,
    default: () => new OrderShippingPoint(),
  },
  stops: {
    type: Array as PropType<Stop[]>,
    default: () => [],
  },
  mileages: {
    type: Object as PropType<Loadable<StopMileageResponse[]>>,
    default: () => ({ loading: false }),
  },
  errorResponse: {
    type: Object as PropType<ErrorResponse>,
    default: () => undefined,
  },
  hasNextStop: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  allowChangingStopType: {
    type: Boolean,
    default: () => false,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  showOrderReferenceNumbers: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  stops,
  mileages,
  errorResponse,
  hasNextStop,
  disabled,
  allowChangingStopType,
  removable,
  showOrderReferenceNumbers,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'autocomplete', isAutocompleteSelection: boolean): void;
  (event: 'remove', stop: Stop): void;
  (event: 'update:model-value', modelValue: Stop): void;
}>();

const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<Stop>) => {
    const Clazz = Object.getPrototypeOf(opts).constructor;
    return new Clazz(opts);
  },
});

const sequenceNumber = toRef(() => internalValue.value.sequenceNumber ?? 0);

const mileage = computed(() => {
  const stopMileageResponse = mileages.value.data?.find(
    (r) => r.originSequenceNumber === sequenceNumber.value,
  );

  return stopMileageResponse?.mileage;
});
</script>

<style>
.stop-view-edit {
  display: flex;
}
</style>
